import { Fragment, ReactNode, Suspense, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button, Result, Spin } from '@arco-design/web-react';
import { useAuthStore } from './store/auth';
import { MONITOR_ROLES } from './layouts/menus';
import Result403 from './components/Result403';

/**
 * 路由壳与守卫：被 `App.tsx` 的 `<Route>`/canonical 路由表引用、但**不属于路由表本身**的实现。
 *
 * 为什么不放在 `App.tsx`：路由表（`lazy(...)` 引入、`CANONICAL_ROUTES`/`CANONICAL_REDIRECTS`、`roleRouteMap`）
 * 是 `scripts/check-page-headers.mjs`、`check-nav-perms.mjs`、`check-feature-index.mjs` 的**文本解析数据源**，
 * 与它无关的组件留在同文件里只会让文件持续变大（见 `docs/conventions/frontend.md §9`）。
 */

/** 懒加载路由的加载态占位 */
export function Loading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
      <Spin size={32} />
    </div>
  );
}

export function Loadable({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/**
 * RBAC 上下文加载失败的守卫态：判据依赖权限码，上下文拉不到时既不能按「无权限」判 403
 * （会误拒合法访问），也不能一直转圈（用户没有任何出口）。这里给出失败原因与重试入口。
 */
export function RbacLoadError({ message, onRetry }: { message: string; onRetry: () => void | Promise<void> }) {
  const [retrying, setRetrying] = useState(false);
  const retry = () => {
    setRetrying(true);
    // 重试成功时本组件随守卫重渲染而卸载，无需再复位 loading
    void Promise.resolve(onRetry()).finally(() => setRetrying(false));
  };
  return (
    <Result
      status="warning"
      title="权限信息加载失败"
      subTitle={message}
      style={{ paddingTop: 120 }}
      extra={
        <Button type="primary" loading={retrying} onClick={retry}>
          重试
        </Button>
      }
    />
  );
}

/**
 * 深链重挂载壳：知识库工作区内部 Tab 走 URL query（useBccTabUrl），页内切换不应重挂载；
 * 外部深链（仪表盘待办跳转、待办中心等）通常伴随 pathname 变化，需重挂载以消费深链。
 * 用 location.pathname 作 key：外部导航（pathname 变）重挂载消费深链，页内 query 变化不重挂载
 *（避免 state/ref 重置导致深链 effect 反复覆盖用户的库/Tab 选择）。
 */
export function KeyedPage({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Fragment key={pathname}>{children}</Fragment>;
}

/**
 * 旧「呼入设置」Landing（/inbound/settings）的拆解落点：该页原把几个互不相关的对象塞进一个 Tab 栏
 * （黑名单=名单、呼叫路由=接线表、技能组=坐席分组），按归属拆散后此地址仅作过渡。
 * 带 ?tab= 的旧深链按 Tab 落到新归属；无 tab（或未知）落到黑名单（拆解后呼入域内的那一项）。
 * 原 ?tab=schedules（时段配置）已随「接线时段」下线，落到它当时的宿主页「进线路由」。
 */
export function LegacyInboundSettingsRedirect() {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  const to =
    tab === 'routes'
      ? '/platform/telephony/routing'
      : tab === 'ivr'
        ? '/platform/telephony/routing/ivr'
        : tab === 'schedules'
          ? '/platform/telephony/routing'
          : tab === 'skill-groups'
            ? '/inbound/skill-groups'
            : '/inbound/blacklist';
  return <Navigate to={to} replace />;
}

/**
 * 旧「系统设置」（/system/sys-settings）的拆解落点：该页原把互不相关的配置塞进一个 Tab 栏
 * （呼叫与媒体 / 数据合规 / 安全认证 / 知识库 / 短信记录 / 规则词表 / 外呼并发），按归属拆散后此地址仅作过渡。
 * 其中「知识库」页签随自建问答库整体下线，已无落点：`?tab=kb|knowledge` 按**未知 tab** 处理（落到默认页）。
 *
 * 为什么必须显式登记：这条旧地址**此前既没有 Route、也没有进重定向表**，命中 `<Route path="*">` →
 * 静默落仪表盘（旧书签与三个浏览器套件都点在这里，却测的是驾驶盘）；而 Registry 与
 * `docs/conventions/information-architecture.md` 都把它当"一对多旧地址"，所以去向只能由本组件按 ?tab= 决定。
 * 带 ?tab= 的旧深链按 Tab 落到新归属；无 tab（或未知）落到「呼叫与媒体」——那是原页面的第一个页签，
 * 也是 Registry 里两条无 tab 认领（通信能力「呼叫与媒体」、外呼「外呼并发」）中最贴近"系统设置"语义的一条。
 */
export function LegacySysSettingsRedirect() {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  const to =
    tab === 'privacy'
      ? '/system/compliance/settings'
      : tab === 'security'
        ? '/system/compliance/security'
        : tab === 'ruleword'
          ? '/bot/intents/ruleword'
          : tab === 'sms'
            ? '/platform/telephony/sms'
            : tab === 'concurrency'
              ? '/outbound/settings/concurrency'
              : '/platform/telephony/capacity';
  return <Navigate to={to} replace />;
}

/**
 * 旧「消息中心」（/workbench/messages）的并入落点：该页原是「已读通知 / 已完成·已失效公告」的独立页，
 * 现并入待办中心成为「通知 / 公告」两个页签（与顶栏通知铃的三个页签一一对应）。
 * 带 ?tab=notice|announce 的旧深链（铃铛的「查看全部…」曾经指向这里）原样带过去，其余落「通知」。
 */
export function LegacyMessagesRedirect() {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  return <Navigate to={`/workbench/tasks?tab=${tab === 'announce' ? 'announce' : 'notice'}`} replace />;
}

/** 大屏路由守卫：URL 带 ?token=（免登只读模式）或 JWT 已登录且角色为 admin/supervisor 方可进入 */
export function ScreenGuard({ children }: { children: JSX.Element }) {
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.user?.role) || 'agent';
  const hasShareToken = new URLSearchParams(window.location.search).has('token');
  if (hasShareToken) return children;
  if (!token) return <Navigate to="/login" replace />;
  // W5-1：大屏仅 admin+supervisor（JWT 分支后端同样校验，避免 agent 误进后 API 401 触发登出）
  if (!MONITOR_ROLES.includes(role)) return <Result403 />;
  return children;
}
